import React from 'react';
import {
	casesService,
	countriesService,
	GET_CASE_ENTITIES,
	GET_CASE_STEPS_STATUS,
	GET_LAWYER_CASE_DETAILS,
	GET_STATES,
} from '@/api';
import { Button } from '@/components/common';
import Icons from '@/const/icons';
import AppRoutes from '@/const/routes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
	FieldValues,
	useForm,
	useFieldArray,
	FormProvider,
} from 'react-hook-form';
import {
	CaseDetailsLawyer,
	Option,
	StepStatusCaseType,
	StatusPhase,
} from '@/interfaces';
import clsx from 'clsx';
import {
	useFormUnsavedChanges,
	useNotification,
	useStepStatus,
	useUser,
} from '@/hooks';
import { useRouter } from 'next/router';
import { casesLawyerService } from '@/api/casesLawyer';
import { CaseContext } from '@/context/CaseContext';
import CaseCompanyInformation from './CaseCompanyInformation';
import CaseBasicInformation from './CaseBasicInformation';
import CaseTeamOfLawyers from './CaseTeamOfLawyers';
import { useAutoSaveCaseDetails } from '@/hooks/autosaved';
import { DEFAULT_CASE_NAME } from './consts';

interface Props {
	stepStatus?: StepStatusCaseType;
	caseDetails?: CaseDetailsLawyer;
	phase?: StatusPhase | undefined;
}

export const CreateCaseForm: React.FC<Props> = ({ phase, caseDetails }) => {
	const { user } = useUser();
	const router = useRouter();
	const { isCaseReadOnly, setSaveStatus } = React.useContext(CaseContext);
	const { toastError } = useNotification();
	const queryClient = useQueryClient();
	const { stepStatus } = useStepStatus({
		stepName: 'Case Details',
		caseSteps: phase?.steps,
	});

	const [isLoading, setIsLoading] = React.useState(false);
	const [applyRule, setApplyRule] = React.useState(false);
	const [caseId, setCaseId] = React.useState<string>();
	const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] =
		React.useState(false);

	const isCreatePath = router.pathname === AppRoutes.LAWYERAPP_CREATE_CASE;
	const isPublished = caseDetails?.is_draft === false;

	const methods = useForm({
		mode: 'onChange',
	});

	const {
		watch,
		reset,
		trigger,
		control,
		handleSubmit,
		// clearErrors,
		// getValues,
		setValue,
		formState: { isDirty, isSubmitting },
	} = methods;
	const formValues = watch();

	useFormUnsavedChanges(
		isDirty && isCreatePath && !isSubmitting && !isSuccessfullySubmitted
	);

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'companies',
	});

	//Entities
	const { data: entities } = useQuery({
		queryKey: [GET_CASE_ENTITIES],
		queryFn: () => casesService.getEntities(),
	});
	//States
	const { data: statesData } = useQuery({
		queryKey: [GET_STATES],
		queryFn: () => countriesService.getStates('1'),
	});

	const listEntityTypes = React.useMemo(() => {
		const listEntities: Option[] = [
			{
				label: 'Select',
				name: '',
				placeholder: true,
			},
		];

		if (entities?.data.items) {
			entities.data.items.forEach((entity) => {
				listEntities.push({
					label: entity.name,
					name: entity.id,
				});
			});
		}

		return listEntities;
	}, [entities]);

	const listStates = React.useMemo(() => {
		const auxStates: Option[] = [
			{
				name: '',
				label: 'Select',
				placeholder: true,
			},
			{
				name: 'all',
				label: 'Select All',
				placeholder: false,
			},
		];

		if (statesData?.data?.states) {
			statesData.data.states.forEach((state) => {
				auxStates.push({
					label: state.name,
					name: `${state.id}`,
				});
			});
		}

		return auxStates;
	}, [statesData]);

	React.useEffect(() => {
		if (applyRule) {
			trigger('lawyerIds');
		}
	}, [applyRule]);

	// const handleSave = async () => {
	// 	clearErrors();
	// 	setApplyRule(false);
	// 	const formData = getValues();
	// 	const fieldsToValidate = []; // ['name'] before
	// 	const filledFields = Object.keys(formData).filter(
	// 		(key) =>
	// 			key !== 'name' &&
	// 			key !== 'elegibleStateIds' &&
	// 			key !== 'companies' &&
	// 			formData[key] !== undefined &&
	// 			formData[key] !== '' &&
	// 			formData[key] !== null
	// 	);

	// 	fieldsToValidate.push(...filledFields);

	// 	const isValid = await trigger(fieldsToValidate);
	// 	if (isValid) {
	// 		return handleCustomSubmit();
	// 	}
	// 	handleScroll();
	// };

	const handleComplete = async () => {
		setApplyRule(true);
		const isValid = await trigger();

		if (isValid) {
			return handleSubmit(onSubmit)();
		}
		handleScroll();
	};

	const handleScroll = () => {
		const container = document.getElementById('global-container');
		container?.scrollTo({
			top: isCreatePath ? 0 : 530,
			behavior: 'smooth',
		});
	};

	// Save Case
	// const handleCustomSubmit = async () => {
	// 	setIsLoading(true);
	// 	const values = getValues();

	// 	let data = getFormData(values);

	// 	if (caseId) {
	// 		data = { ...data, caseId };
	// 	}
	// 	try {
	// 		const response = await casesLawyerService.save(data);
	// 		setCaseId(response.data.case.id);
	// 		debouncedToast();
	// 		params.set('id', response.data.case.id);
	// 		router.replace(`${router.pathname}?${params.toString()}`);
	// 		queryClient.refetchQueries({ queryKey: [GET_CASES] });
	// 		// queryClient.refetchQueries({ queryKey: [GET_LAWYER_CASE_DETAILS] }); // commented due to this has a conflict with the autosave (testing)
	// 		queryClient.refetchQueries({ queryKey: [GET_CASE_STEPS_STATUS] });
	// 	} catch (error) {
	// 		toastError('An error occurred while saving. Please try again.');
	// 	} finally {
	// 		setTimeout(() => {
	// 			setIsLoadingAutosaved(false);
	// 			setIsLoading(false);
	// 		}, 1000);
	// 	}
	// };

	// const debouncedToast = useDebouncedCallback(() => {
	// 	toastInfo('Your case details have been saved.', 'bottom-center', Slide);
	// }, 5000); //5seg

	// Complete Case
	const onSubmit = async (values: FieldValues) => {
		setIsLoading(true);
		setSaveStatus({ isError: false });

		let data = getFormData(values);
		if (caseId) {
			data = { ...data, caseId };
		}
		try {
			const response = await casesLawyerService.complete(data);
			setSaveStatus({ isError: false });
			setCaseId(response.data.case.id);
			// debouncedToast.cancel(); //  Suppress save toast on complete action
			if (isCreatePath) {
				setIsSuccessfullySubmitted(true);
				router.push(
					`/lawyerapp/cases/${response.data.case.id}/applicant-intake`
				);
				return;
			}
			const completedCaseId = response.data.case.id;
			if (completedCaseId) {
				queryClient.refetchQueries({
					queryKey: [GET_LAWYER_CASE_DETAILS, completedCaseId],
				});
				queryClient.refetchQueries({
					queryKey: [GET_CASE_STEPS_STATUS, completedCaseId],
				});
			}
			// params.set('id', response.data.case.id);
			// router.replace(`${router.pathname}?${params.toString()}`);
		} catch (error) {
			setSaveStatus({ isError: true });
			toastError(
				'An error occurred while confirming completion. Please try again.'
			);
		} finally {
			setTimeout(() => {
				setIsLoading(false);
			}, 1000);
		}
	};

	const getFormData = (values: FieldValues) => {
		return {
			caseType: values.caseType || null,
			caseName: values.name || DEFAULT_CASE_NAME, // by default
			statusId: values.status ? Number(values.status) : null,
			categoryId: values.category ? Number(values.category) : null,
			elegibleStateIds: Array.isArray(values?.elegibleStateIds)
				? Array.from(
						new Set<number>(
							values.elegibleStateIds.map((state: Option) => Number(state.name))
						)
				  )
				: [],
			brandsData: Array.isArray(values.companies)
				? values.companies.filter(
						(company: CompanyI) =>
							company.brandId &&
							company.entityType &&
							company.defendantIncorporation &&
							company.princpialPlaceOfBusiness
				  )
				: [],
			lawyerIds: values.lawyerIds,
			caseId: caseId || '',
		};
	};

	const isFilled = (value: any, key?: string): boolean => {
		if (key === 'amount') {
			return true;
		}

		if (Array.isArray(value)) {
			return value.length > 0 && value.every((item) => isFilled(item));
		}

		if (typeof value === 'object' && value !== null) {
			return Object.entries(value).every(([childKey, childValue]) =>
				isFilled(childValue, childKey)
			);
		}

		return value !== null && value !== undefined && String(value).trim() !== '';
	};

	const areAllFieldsFilled = React.useMemo(() => {
		return isFilled(formValues);
	}, [formValues]);

	const isComplete = stepStatus === 'completed' || stepStatus === 'approved';

	// ---------------- Auto Save ------------------//
	const { isLoadingAutosaved } = useAutoSaveCaseDetails({
		caseDetails,
		handleSave: handleSubmit(onSubmit),
		control,
		autoSavedActived: !isCreatePath,
	});

	React.useEffect(() => {
		setSaveStatus({ isSaving: isLoading || isLoadingAutosaved });
	}, [isLoading, isLoadingAutosaved, setSaveStatus]);

	React.useEffect(() => {
		const mappedStates =
			caseDetails?.states?.map((state) => ({
				label: state.name,
				name: state.id.toString(),
			})) || [];
		const mappedCompanies = getMappedCompanies(caseDetails);
		const existingLawyerIds =
			caseDetails?.lawyers?.map((lawyer) => lawyer.id?.toString()) || [];

		// Add current user ID if it's not already included
		const updatedLawyerIds = user?.id
			? Array.from(new Set([...existingLawyerIds, user.id.toString()]))
			: existingLawyerIds;
		if (!isDirty) {
			const dataUpdated = {
				name:
					caseDetails?.title === DEFAULT_CASE_NAME
						? ''
						: caseDetails?.title || '',
				category: caseDetails?.category_id?.toString() || '',
				caseType: caseDetails?.case_type || '',
				status: caseDetails?.status_id?.toString() || '',
				companies: mappedCompanies,
				elegibleStateIds: mappedStates,
				lawyerIds: updatedLawyerIds,
				// amount: caseDetails?.advertising_spent || '',
				// targetGoals: caseDetails?.applicants_target_goal || '',
			};
			reset(dataUpdated);
			setCaseId(caseDetails?.id || '');
		}

		setValue('companies', mappedCompanies);
	}, [caseDetails, listEntityTypes, listStates, user]); // this dependencies have to be here or the fields for company information wont fill on first render

	return (
		<>
			<FormProvider {...methods}>
				<form className="space-y-6">
					<CaseBasicInformation
						caseDetails={caseDetails}
						listStates={listStates}
						applyRule={applyRule}
						isPublished={isPublished}
					/>
					<CaseCompanyInformation
						caseDetails={caseDetails}
						listEntityTypes={listEntityTypes}
						append={append}
						remove={remove}
						fields={fields}
						listStates={listStates}
						applyRule={applyRule}
						isPublished={isPublished}
					/>
					<CaseTeamOfLawyers
						caseDetails={caseDetails}
						applyRule={applyRule}
						isPublished={isPublished}
					/>

					{isCreatePath && (
						<div className="flex justify-end gap-6 mt-8">
							<Button
								label="Continue to Next Phase"
								decoration="primary"
								size="medium"
								onClick={handleComplete}
								iconRight
								icon={Icons.arrowRight}
								disabled={isComplete || isCaseReadOnly || isLoading}
								classNameButton="!size-4"
								className={clsx({
									'opacity-60 cursor-default': !areAllFieldsFilled,
								})}
							/>
						</div>
					)}
				</form>
			</FormProvider>
		</>
	);
};

export type CompanyI = {
	caseBrandId?: number;
	brandId: string;
	entityType: string;
	defendantIncorporation: string;
	princpialPlaceOfBusiness: string;
};

export const getMappedCompanies = (
	caseData?: CaseDetailsLawyer
): CompanyI[] => {
	const mappedCompanies: CompanyI[] =
		!caseData?.brands || caseData.brands.length === 0
			? [
					{
						brandId: '',
						entityType: '',
						defendantIncorporation: '',
						princpialPlaceOfBusiness: '',
					},
			  ]
			: caseData.brands
					.sort((a, b) => a.caseBrandId - b.caseBrandId)
					.map((brand) => ({
						caseBrandId: brand.caseBrandId,
						brandId: brand.id,
						entityType: brand.entity_type,
						defendantIncorporation:
							brand.defendant_incorporation.id?.toString(),
						princpialPlaceOfBusiness:
							brand.principal_place_of_business.name?.toString(), // the backend is bringing the id in name for this one
					}));
	return mappedCompanies;
};
